import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link2, CheckCircle, ExternalLink, Loader2, Unplug } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";

interface Integration {
  name: string;
  category: "ERP" | "AP";
  logo: string;
  connected: boolean;
  description: string;
  demoUsername?: string;
  demoPassword?: string;
}

const defaultIntegrations: Integration[] = [
  {
    name: "SAP",
    category: "ERP",
    logo: "🔷",
    connected: false,
    description: "Enterprise resource planning system",
    // Demo credentials removed for production
    // demoUsername: "sap_admin",
    // demoPassword: "sap_key_2026",
  },
  {
    name: "Oracle NetSuite",
    category: "ERP",
    logo: "🔶",
    connected: false,
    description: "Cloud-based ERP solution",
    // Demo credentials removed for production
    // demoUsername: "netsuite_api",
    // demoPassword: "ns_secret_abc123",
  },
  {
    name: "Microsoft Dynamics",
    category: "ERP",
    logo: "🟦",
    connected: false,
    description: "Business applications platform",
    demoUsername: "dynamics_user",
    demoPassword: "dyn_pass_xyz",
  },
  {
    name: "QuickBooks",
    category: "AP",
    logo: "🟩",
    connected: true,
    description: "Accounting software",
    demoUsername: "qb_admin@company.com",
    demoPassword: "qb_api_key_789",
  },
  {
    name: "Bill.com",
    category: "AP",
    logo: "🟪",
    connected: false,
    description: "Accounts payable automation",
    demoUsername: "bill_api",
    demoPassword: "bill_secret_456",
  },
  {
    name: "Coupa",
    category: "AP",
    logo: "🔴",
    connected: false,
    description: "Spend management platform",
    demoUsername: "coupa_integrator",
    demoPassword: "coupa_token_def",
  },
];

interface IntegrationsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function IntegrationsModal({ open, onOpenChange }: IntegrationsModalProps) {
  const [integrations, setIntegrations] = useState<Integration[]>(defaultIntegrations);
  const [selectedIntegration, setSelectedIntegration] = useState<Integration | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const { toast } = useToast();

  const connectedCount = integrations.filter((i) => i.connected).length;

  const handleIntegrationClick = (integration: Integration) => {
    setSelectedIntegration(integration);
    // Clear form when selecting integration
    setUsername("");
    setPassword("");
  };

  const handleConnect = async () => {
    if (!selectedIntegration) return;
    setIsConnecting(true);
    await new Promise((resolve) => setTimeout(resolve, 1500));

    setIntegrations((prev) =>
      prev.map((i) => (i.name === selectedIntegration.name ? { ...i, connected: true } : i))
    );

    toast({
      title: "Connected",
      description: `${selectedIntegration.name} has been connected successfully.`,
    });

    setIsConnecting(false);
    setSelectedIntegration(null);
  };

  const handleDisconnect = async () => {
    if (!selectedIntegration) return;
    setIsConnecting(true);
    await new Promise((resolve) => setTimeout(resolve, 1000));

    setIntegrations((prev) =>
      prev.map((i) => (i.name === selectedIntegration.name ? { ...i, connected: false } : i))
    );

    toast({
      title: "Disconnected",
      description: `${selectedIntegration.name} has been disconnected.`,
    });

    setIsConnecting(false);
    setSelectedIntegration(null);
  };

  const sortedIntegrations = [...integrations].sort((a, b) => {
    if (a.connected && !b.connected) return -1;
    if (!a.connected && b.connected) return 1;
    return 0;
  });

  return (
    <>
      <Dialog open={open && !selectedIntegration} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5 text-primary" />
              Integrations
            </DialogTitle>
            <DialogDescription>
              Connect your ERP & AP systems • {connectedCount} connected
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 py-4">
            {sortedIntegrations.map((integration) => (
              <div
                key={integration.name}
                onClick={() => handleIntegrationClick(integration)}
                className={`relative p-4 rounded-lg border transition-all cursor-pointer hover:border-primary/50 hover:shadow-md ${
                  integration.connected
                    ? "bg-success/10 border-success/40 ring-2 ring-success/20"
                    : "bg-muted/30 border-border opacity-75 hover:opacity-100"
                }`}
              >
                {integration.connected ? (
                  <CheckCircle className="absolute top-2 right-2 w-4 h-4 text-success" />
                ) : (
                  <Badge
                    variant="outline"
                    className="absolute top-2 right-2 text-[9px] px-1 py-0 text-muted-foreground border-muted-foreground/30"
                  >
                    Inactive
                  </Badge>
                )}
                <div className="text-2xl mb-2">{integration.logo}</div>
                <p
                  className={`text-sm font-medium ${integration.connected ? "text-foreground" : "text-muted-foreground"}`}
                >
                  {integration.name}
                </p>
                <Badge
                  variant="outline"
                  className={`mt-1 text-[10px] px-1.5 py-0 ${integration.connected ? "" : "opacity-60"}`}
                >
                  {integration.category}
                </Badge>
                {integration.connected && (
                  <div className="mt-2 flex items-center gap-1 text-xs text-success">
                    <span>Active</span>
                    <ExternalLink className="w-3 h-3" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Integration Detail Modal */}
      <Dialog open={!!selectedIntegration} onOpenChange={() => setSelectedIntegration(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-2xl">{selectedIntegration?.logo}</span>
              {selectedIntegration?.connected ? "Manage" : "Connect"} {selectedIntegration?.name}
            </DialogTitle>
            <DialogDescription>{selectedIntegration?.description}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="int-username">Username / API Key</Label>
              <Input
                id="int-username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder={`Enter your ${selectedIntegration?.name} username`}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="int-password">Password / Secret</Label>
              <Input
                id="int-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
              />
            </div>

            {selectedIntegration?.connected && (
              <div className="p-3 bg-success/10 rounded-lg flex items-center gap-2 text-sm text-success">
                <CheckCircle className="w-4 h-4" />
                Currently connected
              </div>
            )}
          </div>

          <DialogFooter className="flex gap-2">
            {selectedIntegration?.connected && (
              <Button
                variant="destructive"
                onClick={handleDisconnect}
                disabled={isConnecting}
                className="mr-auto"
              >
                {isConnecting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Unplug className="w-4 h-4 mr-2" />
                )}
                Disconnect
              </Button>
            )}
            <Button variant="outline" onClick={() => setSelectedIntegration(null)}>
              Cancel
            </Button>
            <Button onClick={handleConnect} disabled={isConnecting || !username}>
              {isConnecting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {selectedIntegration?.connected ? "Saving..." : "Connecting..."}
                </>
              ) : selectedIntegration?.connected ? (
                "Save Changes"
              ) : (
                "Connect"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
